/**
 * LoanDetailsSheet Component
 *
 * A sliding sheet that displays detailed loan information with tabbed navigation.
 * Tabs are displayed on the left side for easy navigation between:
 * - Form 1008 (Underwriting Summary)
 * - Product/Pricing (Rate Lock and LLPA details)
 * - Loan Estimate (Parsed LE viewer)
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { LoanEmailHistory } from './email-history/LoanEmailHistory';
import { Form1008Content } from './Form1008Content';
import { DisclosureReminderControls } from './DisclosureReminderControls';
import { ProductPricingWorkspace } from './pricing-v2';
import { getLockVersions, type LockVersion } from '@/services/loanPricingService';
import {
  activateDeliveryLockVersion,
  applyDeliveryLockToLoan,
  DeliveryLockActivationError,
  getActiveDeliveryLockSelection,
  getActiveDeliveryLockSelectionStrict,
  setActiveDeliveryLockFromVersion,
  type DeliveryLockSelection
} from '@/services/deliveryLockService';
import { getReviewableMaterialRuns, isSelectableLockVersion } from './pricing-v2/lockMaterialRuns';
import { lockRateValue } from '@/services/lockNetBuyRate';
import { ApprovalLetterTab } from './ApprovalLetterTab';
import { MyCompensationTab } from './MyCompensationTab';
import { InvestorDeliveryTab } from './InvestorDeliveryTab';
import { LoanTeamTab } from './LoanTeamTab';
import { ClarifiExportButton } from '../ClarifiExportButton';
import { LoanEstimateViewer, LEViewerEmptyState } from '@/components/loan-estimate';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useBeforeUnloadWarning } from '@/hooks/useBeforeUnloadWarning';
import { isLoanAccessDeniedError } from './loanScope';
import { DocOrderTab } from './doc-order/DocOrderTab';
import { isDocOrderChannel } from './doc-order/core';
import { FraudAlertsTab } from './FraudAlertsTab';
import { AppraisalTab } from './appraisal/AppraisalTab';
import type { LoanDetailsTab } from './loanCenterDeepLink';
import type { ProspectLoan } from './types';
import {
  FileText,
  DollarSign,
  Lock,
  Users,
  ClipboardCheck,
  FileSpreadsheet,
  Wallet,
  Truck,
  FileSignature,
  Loader2,
  ShieldAlert,
  House,
  Mail
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatPercent, toNumberOrNull, canonEqual } from './utils/loanValueHelpers';
import { supabase } from '@/utils/supabase';
import { toast } from 'sonner';
import {
  getLoanFraudAlerts,
  type FraudAlert
} from '@/services/fraudAlertService';

type TabId = LoanDetailsTab;

/** Where the user was going when unsaved Product & Pricing edits stopped them. */
type PendingLeave = { kind: 'tab'; tab: TabId } | { kind: 'close' };

interface LoanDetailsSheetProps {
  isOpen: boolean;
  onClose: () => void;
  loan: ProspectLoan;
  /** Initial tab to open (defaults to 'form1008') */
  initialTab?: TabId;
  initialAppraisalReportId?: string;
  initialFeeVerificationId?: string;
  onCloseAutoFocus?: (event: Event) => void;
  /** Propagate optimistic updates to the parent table's prospect list */
  onLoanUpdate?: (loanId: string, updates: Partial<ProspectLoan>) => void;
  /**
   * Refetch callback for the parent grid (e.g. ProspectDashboard's
   * usePaginatedProspects). The grid subscribes only to encompass_loans
   * realtime, which does NOT fire when the active delivery lock changes —
   * so after Set Active, we must explicitly refetch the grid to reflect
   * the new active-lock-derived investor on the row.
   */
  onActiveLockChanged?: () => void;
  /** Add this loan to the shared data-export basket and open the workspace. */
  onOpenDataExports?: () => void;
  /** Open this loan's document workspace from Fraud Alerts evidence. */
  onOpenLoanDocuments?: () => void;
}

interface TabConfig {
  id: TabId;
  label: string;
  icon: React.ElementType;
  description: string;
}

const TABS: TabConfig[] = [
  {
    id: 'form1008',
    label: 'Form 1008',
    icon: FileText,
    description: 'Underwriting Summary'
  },
  {
    id: 'team',
    label: 'Loan Team',
    icon: Users,
    description: 'People & Roles'
  },
  {
    id: 'pricing',
    label: 'Product & Pricing',
    icon: DollarSign,
    description: 'Rate Lock & LLPA'
  },
  {
    id: 'approval',
    label: 'Approval Letter',
    icon: ClipboardCheck,
    description: 'Conditional Approval'
  },
  {
    id: 'loanEstimate',
    label: 'Loan Estimate',
    icon: FileSpreadsheet,
    description: 'TRID Loan Estimate'
  },
  {
    id: 'delivery',
    label: 'Delivery',
    icon: Truck,
    description: 'Investor Delivery'
  },
  {
    id: 'docorder',
    label: 'Doc Order',
    icon: FileSignature,
    description: 'Closing Docs Request'
  },
  {
    id: 'fraudAlerts',
    label: 'Fraud Alerts',
    icon: ShieldAlert,
    description: 'Amended Return Review'
  },
  {
    id: 'appraisal',
    label: 'Appraisal',
    icon: House,
    description: 'Report, Activity & Audit'
  },
  { id: 'communications', label: 'Communications', icon: Mail, description: 'Emails & reminders' },
  {
    id: 'compensation',
    label: 'My Compensation',
    icon: Wallet,
    description: 'LO Earnings'
  }
];

const PRODUCT_PRICING_OVERRIDE_KEYS: Array<keyof ProspectLoan> = [
  'discount_point_percent',
  'discount_point_amount',
  'origination_fee_percent',
  'trans_details_non_specific_lender_credit'
];

function displayHeaderValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return String(value);
}

function isPresent(value: unknown): boolean {
  return value !== null && value !== undefined;
}

function formatWholeCurrency(value: unknown): string {
  const numericValue = toNumberOrNull(value);
  if (numericValue === null || numericValue === undefined) {
    return '-';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(numericValue);
}

function PricingHeaderSummary({ loan }: { loan: ProspectLoan }) {
  const program = displayHeaderValue(loan.rate_lock_loan_program);
  const docType = loan.cx_doc_type ?? loan.rate_lock_documentation_type;
  const documentation = displayHeaderValue(docType);
  const hpml = displayHeaderValue(loan.cx_hpml_result).trim().toUpperCase();
  const hpmlTone =
    hpml === 'PASS'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
      : hpml !== '-'
        ? 'border-rose-200 bg-rose-50 text-rose-800'
        : 'border-white/20 bg-white/10 text-white/80';
  const hpmlDot =
    hpml === 'PASS'
      ? 'bg-emerald-500'
      : hpml !== '-'
        ? 'bg-rose-500'
        : 'bg-white/50';

  return (
    <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
      <div className="min-w-0 rounded-lg border border-white/15 bg-white/15 px-4 py-3">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-white/70">
          <span className="h-1.5 w-1.5 rounded-full bg-white/70" />
          Product
        </div>
        <div className="mt-1.5 flex min-w-0 items-center gap-3">
          <div className="min-w-0 flex-1 truncate text-[15px] font-semibold leading-snug text-white" title={program}>
            {program}
          </div>
          <div className="inline-flex min-w-0 max-w-[40%] shrink-0 items-center rounded-full border border-white/15 bg-white/10 px-2.5 py-1 text-xs font-medium text-white/85">
            <span className="truncate">{documentation}</span>
          </div>
        </div>
      </div>

      <div className="min-w-0 rounded-md border border-white/10 bg-white/10 px-4 py-3 lg:min-w-36">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-white/65">
          HPML
        </div>
        <span className={cn('mt-1 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold', hpmlTone)}>
          <span className={cn('h-1.5 w-1.5 rounded-full', hpmlDot)} />
          {hpml}
        </span>
      </div>
    </div>
  );
}

export function LoanDetailsSheet({
  isOpen,
  onClose,
  loan: initialLoan,
  initialTab = 'form1008',
  initialAppraisalReportId,
  initialFeeVerificationId,
  onCloseAutoFocus,
  onLoanUpdate,
  onActiveLockChanged,
  onOpenDataExports,
  onOpenLoanDocuments
}: LoanDetailsSheetProps) {
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  // Product & Pricing reports unsaved edits. Switching tabs or closing the
  // sheet then asks first (a reload gets the browser's own prompt); a save
  // that was accepted and is still syncing is not an unsaved edit.
  const [pricingHasUnsavedEdits, setPricingHasUnsavedEdits] = useState(false);
  const [pendingLeave, setPendingLeave] = useState<PendingLeave | null>(null);
  const leaveFocusRef = useRef<HTMLElement | null>(null);

  // --- Single-source-of-truth state model ---
  // initialLoan comes from the parent list; authoritativeLoan is a direct row
  // refresh used by tabs where stale pricing values are costly.
  // optimisticOverrides holds successful local saves until the server catches up.
  const [authoritativeLoan, setAuthoritativeLoan] = useState<ProspectLoan | null>(null);
  const [optimisticOverrides, setOptimisticOverrides] = useState<Partial<ProspectLoan>>({});
  const authoritativeLoanRequestIdRef = useRef(0);
  const latestInitialLoanRef = useRef(initialLoan);
  latestInitialLoanRef.current = initialLoan;

  // Clear overrides + inspected lock version when switching to a different loan
  const [lockVersions, setLockVersions] = useState<LockVersion[]>([]);
  const [selectedLockVersionId, setSelectedLockVersionId] = useState<string | null>(null);
  const [deliveryLockSelection, setDeliveryLockSelection] = useState<DeliveryLockSelection | null>(null);
  const [fraudAlerts, setFraudAlerts] = useState<FraudAlert[]>([]);
  const [fraudAlertsLoading, setFraudAlertsLoading] = useState(false);
  const [fraudAlertsLoaded, setFraudAlertsLoaded] = useState(false);
  const [fraudAlertsError, setFraudAlertsError] = useState<string | null>(null);
  const fraudAlertsRequestIdRef = useRef(0);

  useEffect(() => {
    fraudAlertsRequestIdRef.current += 1;
    setPricingHasUnsavedEdits(false);
    setPendingLeave(null);
    setAuthoritativeLoan(null);
    setOptimisticOverrides({});
    setSelectedLockVersionId(null);
    setDeliveryLockSelection(null);
    setFraudAlerts([]);
    setFraudAlertsLoaded(false);
    setFraudAlertsError(null);
  }, [initialLoan.id]);

  const refreshFraudAlerts = useCallback(async () => {
    const requestId = ++fraudAlertsRequestIdRef.current;
    const isCurrentRequest = () => fraudAlertsRequestIdRef.current === requestId;

    if (!initialLoan.encompass_loan_id) {
      if (isCurrentRequest()) {
        setFraudAlerts([]);
        setFraudAlertsLoading(false);
        setFraudAlertsLoaded(true);
        setFraudAlertsError(null);
      }
      return;
    }

    setFraudAlertsLoading(true);
    setFraudAlertsError(null);
    try {
      const alerts = await getLoanFraudAlerts(initialLoan.encompass_loan_id);
      if (isCurrentRequest()) {
        setFraudAlerts(alerts);
      }
    } catch (error) {
      if (isCurrentRequest()) {
        setFraudAlerts([]);
        setFraudAlertsError(error instanceof Error ? error.message : 'Unable to load fraud alerts.');
      }
    } finally {
      if (isCurrentRequest()) {
        setFraudAlertsLoading(false);
        setFraudAlertsLoaded(true);
      }
    }
  }, [initialLoan.encompass_loan_id]);

  useEffect(() => {
    if (!isOpen) {
      fraudAlertsRequestIdRef.current += 1;
      return;
    }
    void refreshFraudAlerts();

    return () => {
      fraudAlertsRequestIdRef.current += 1;
    };
  }, [isOpen, refreshFraudAlerts]);

  useEffect(() => {
    setAuthoritativeLoan(prev => {
      if (!prev || prev.id !== initialLoan.id) {
return prev;
}
      const prevTime = Date.parse(prev.updated_at ?? '');
      const incomingTime = Date.parse(initialLoan.updated_at ?? '');
      if (Number.isFinite(incomingTime) && (!Number.isFinite(prevTime) || incomingTime > prevTime)) {
        return initialLoan;
      }
      return prev;
    });
  }, [initialLoan]);

  const refreshAuthoritativeLoan = useCallback(async () => {
    const requestId = ++authoritativeLoanRequestIdRef.current;
    if (!initialLoan.id) {
return;
}
    const { data, error } = await supabase
      .from('encompass_loans')
      .select('*')
      .eq('id', initialLoan.id)
      .maybeSingle();

    // Only the newest read may land. Reads finish in any order, and an older
    // row would roll the screen back (the draft then takes an unsaved edit
    // that matches it as unchanged, and the next read replaces it).
    if (requestId !== authoritativeLoanRequestIdRef.current || initialLoan.id !== latestInitialLoanRef.current.id) {
      return;
    }
    if (error) {
      if (isLoanAccessDeniedError(error)) {
        setAuthoritativeLoan(null);
        setOptimisticOverrides({});
        onClose();
        return;
      }
      console.warn('[LoanDetailsSheet] Failed to refresh authoritative loan row', error);
      return;
    }
    if (!data) {
      // RLS returns zero rows (without an error) after a team reassignment.
      // Never retain the parent list's stale copy in an open details sheet.
      setAuthoritativeLoan(null);
      setOptimisticOverrides({});
      onClose();
      return;
    }
    setAuthoritativeLoan(prev => {
      const parentLoan = latestInitialLoanRef.current;
      if (data.id !== parentLoan.id) {
        return prev;
      }
      // The parent list refreshes independently. Request order alone cannot
      // stop a slower sheet read from replacing a newer row already received
      // from that list (including before the first sheet read has finished).
      let newest = data as ProspectLoan;
      let newestTime = Date.parse(newest.updated_at ?? '');
      for (const known of [prev, parentLoan]) {
        if (known?.id !== newest.id) {
          continue;
        }
        const knownTime = Date.parse(known.updated_at ?? '');
        if (Number.isFinite(knownTime) && (!Number.isFinite(newestTime) || knownTime > newestTime)) {
          newest = known;
          newestTime = knownTime;
        }
      }
      return newest;
    });
  }, [initialLoan.id, onClose]);

  useEffect(() => {
    if (!isOpen) {
return;
}
    void refreshAuthoritativeLoan();
  }, [activeTab, isOpen, refreshAuthoritativeLoan]);

  useEffect(() => {
    if (!isOpen || !initialLoan.id) {
return;
}
    const channel = supabase
      .channel(`loan-details:${initialLoan.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'encompass_loans',
          filter: `id=eq.${initialLoan.id}`
        },
        () => {
          void refreshAuthoritativeLoan();
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [initialLoan.id, isOpen, refreshAuthoritativeLoan]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const revalidateOnFocus = () => {
      if (document.visibilityState === 'visible') {
        void refreshAuthoritativeLoan();
      }
    };
    window.addEventListener('focus', revalidateOnFocus);
    document.addEventListener('visibilitychange', revalidateOnFocus);
    return () => {
      window.removeEventListener('focus', revalidateOnFocus);
      document.removeEventListener('visibilitychange', revalidateOnFocus);
    };
  }, [isOpen, refreshAuthoritativeLoan]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const interval = window.setInterval(() => {
      void refreshAuthoritativeLoan();
    }, 60_000);
    return () => window.clearInterval(interval);
  }, [isOpen, refreshAuthoritativeLoan]);

  const serverLoan = authoritativeLoan?.id === initialLoan.id ? authoritativeLoan : initialLoan;

  // Derive display loan = server truth + optimistic overrides
  const loan = useMemo(() => {
    if (Object.keys(optimisticOverrides).length === 0) {
return serverLoan;
}
    return { ...serverLoan, ...optimisticOverrides };
  }, [serverLoan, optimisticOverrides]);

  useEffect(() => {
    if (Object.keys(optimisticOverrides).length === 0) {
return;
}
    setOptimisticOverrides(prev => {
      const next = { ...prev };
      let changed = false;
      for (const key of PRODUCT_PRICING_OVERRIDE_KEYS) {
        if (key in next) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [serverLoan.id, serverLoan.updated_at, optimisticOverrides]);

  // Reconcile: clear override keys whose canonical server value now matches
  useEffect(() => {
    if (Object.keys(optimisticOverrides).length === 0) {
return;
}
    setOptimisticOverrides(prev => {
      let changed = false;
      const nextEntries = Object.entries(prev).filter(([key, val]) => {
        const shouldKeep = !canonEqual(serverLoan[key as keyof ProspectLoan], val);
        if (!shouldKeep) {
          changed = true;
        }
        return shouldKeep;
      });
      const next = Object.fromEntries(nextEntries) as Partial<ProspectLoan>;
      return changed ? next : prev;
    });
  }, [serverLoan, serverLoan.updated_at, optimisticOverrides]);

  // Child callback writes to overrides AND propagates to parent table
  const handleLoanUpdate = useCallback((updates: Partial<ProspectLoan>) => {
    setOptimisticOverrides(prev => ({ ...prev, ...updates }));
    onLoanUpdate?.(initialLoan.id, updates);
  }, [initialLoan.id, onLoanUpdate]);

  // Only reset tab when opening a DIFFERENT loan (not on data updates)
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialLoan.id, initialTab]);

  // Load pure Encompass/OB lock versions when pricing tab is active.
  useEffect(() => {
    let cancelled = false;
    setLockVersions([]);
    setSelectedLockVersionId(null);

    if (!isOpen || activeTab !== 'pricing' || !loan?.encompass_loan_id) {
      return () => {
        cancelled = true;
      };
    }

    getLockVersions(loan.encompass_loan_id)
      .then((versions) => {
        if (!cancelled) {
          setLockVersions(versions);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.warn('[LoanDetailsSheet] Failed to load lock versions', error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, isOpen, loan?.encompass_loan_id]);

  // Load active delivery-lock selection whenever the sheet is open. NOT gated
  // by activeTab anymore: every read-only tab now reads `effectiveLoan` (the
  // overlay-applied loan), so the selection must be loaded regardless of which
  // tab is showing — otherwise non-pricing tabs surface stale Encompass values
  // (e.g., "A&D Mortgage") even when the active lock has a different investor.
  useEffect(() => {
    let cancelled = false;
    setDeliveryLockSelection(null);

    if (!isOpen || !loan?.encompass_loan_id) {
      return () => {
        cancelled = true;
      };
    }
    const requestedEncompassLoanId = loan.encompass_loan_id;
    getActiveDeliveryLockSelection(loan.encompass_loan_id)
      .then((selection) => {
        if (!cancelled) {
          setDeliveryLockSelection(
            selection?.encompassLoanId === requestedEncompassLoanId ? selection : null
          );
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.warn('[LoanDetailsSheet] Failed to load delivery lock selection', error);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, loan?.encompass_loan_id]);

  // Lock versions are the saved Encompass/OB lock-data ledger. Local pricing
  // edits are intentionally not part of this list.
  const visibleLockVersions = useMemo(() => {
    return lockVersions;
  }, [lockVersions]);

  // Stale selection guard: if the inspected lock version disappeared after refresh,
  // reset to the editable active/current view.
  useEffect(() => {
    if (selectedLockVersionId && !visibleLockVersions.some(s => s.id === selectedLockVersionId)) {
      setSelectedLockVersionId(null);
    }
  }, [visibleLockVersions, selectedLockVersionId]);

  // Compute effective loan for pricing tab. Saved lock versions overlay only
  // lock-derived fields; local pricing fields stay live so inspecting or
  // activating a version never blanks concessions, discount, BPC, lender
  // credit, or LOCR.
  const selectedLockVersion = selectedLockVersionId
    ? visibleLockVersions.find(s => s.id === selectedLockVersionId) ?? null
    : null;

  const activeSelectionForCurrentLoan = useMemo(() => {
    if (deliveryLockSelection?.encompassLoanId !== loan.encompass_loan_id) {
      return null;
    }
    return deliveryLockSelection;
  }, [deliveryLockSelection, loan.encompass_loan_id]);

  // Set Active par guard. Off (or still loading) = today's behaviour: read-only
  // inspection, direct RPC activation, no gating.
  const parGuard = useFeatureFlag('pricing_set_active_par_guard');
  const parGuardEnabled = parGuard.enabled;

  // Material runs drive candidate mode and the workspace key. The run's
  // setActiveRepresentative is the row Set Active actually activates, so the
  // overlay and the server check target the same row.
  const materialRuns = useMemo(
    () => getReviewableMaterialRuns(visibleLockVersions, activeSelectionForCurrentLoan?.lockVersionId ?? null),
    [visibleLockVersions, activeSelectionForCurrentLoan?.lockVersionId]
  );
  const selectedRun = useMemo(
    () => (selectedLockVersionId
      ? materialRuns.find((run) => run.snapshots.some((s) => s.id === selectedLockVersionId)) ?? null
      : null),
    [materialRuns, selectedLockVersionId]
  );
  const activeRun = useMemo(() => materialRuns.find((run) => run.hasActive) ?? null, [materialRuns]);
  const candidateVersion = useMemo<LockVersion | null>(() => {
    if (!parGuardEnabled || !selectedRun || selectedRun.hasActive) {
      return null;
    }
    const target = selectedRun.setActiveRepresentative;
    return isSelectableLockVersion(target) ? target : null;
  }, [parGuardEnabled, selectedRun]);

  // Active-lock-overlaid loan for ALL read-only tabs (form1008, team, approval,
  // loan estimate, delivery, compensation). Without this, non-pricing tabs read
  // raw `loan.investor_name` and surface stale Encompass values even after Set
  // Active in Product & Pricing. The overlay is field-scoped via
  // RATE_LOCK_SNAPSHOT_KEYS, so LE/CD/local-pricing fields stay live.
  const effectiveLoan: ProspectLoan = useMemo(() => {
    return applyDeliveryLockToLoan(loan, activeSelectionForCurrentLoan) as ProspectLoan;
  }, [loan, activeSelectionForCurrentLoan]);

  const pricingLoan: ProspectLoan = useMemo(() => {
    if (candidateVersion) {
      // Candidate mode: the lock fields of the row Set Active will activate;
      // local pricing stays live (and editable).
      return { ...loan, ...candidateVersion.rateLockFields } as ProspectLoan;
    }
    if (!selectedLockVersion) {
      return effectiveLoan;
    }
    // Historical inspection: pure snapshot, NOT layered over active-lock fields.
    return { ...loan, ...selectedLockVersion.rateLockFields } as ProspectLoan;
  }, [loan, candidateVersion, selectedLockVersion, effectiveLoan]);

  const applyActivatedSelection = useCallback((next: DeliveryLockSelection) => {
    setDeliveryLockSelection(next);
    setSelectedLockVersionId(null);
    // Refresh the grid first (longer in-flight), then the sheet's local
    // authoritative row. Active-delivery-lock changes don't fire the
    // encompass_loans realtime channel, so this is the propagation path
    // for the Loan Center row's investor column.
    onActiveLockChanged?.();
    void refreshAuthoritativeLoan();
  }, [onActiveLockChanged, refreshAuthoritativeLoan]);

  const handleUseLockVersionAsDeliveryLock = useCallback(async (version: LockVersion) => {
    if (!parGuardEnabled) {
      const next = await setActiveDeliveryLockFromVersion({
        loanId: loan.id,
        encompassLoanId: loan.encompass_loan_id,
        organizationId: loan.organization_id,
        version
      });
      applyActivatedSelection(next);
      toast.success('Active delivery lock updated. No email sent.');
      return;
    }
    // Guarded path: the server checks par against this lock version first.
    try {
      const next = await activateDeliveryLockVersion({
        loanId: loan.id,
        encompassLoanId: loan.encompass_loan_id,
        lockVersionId: version.id,
        expectedActiveLockVersionId: activeSelectionForCurrentLoan?.lockVersionId ?? null
      });
      applyActivatedSelection(next);
      toast.success('Active delivery lock updated. No email sent.');
    } catch (error) {
      if (error instanceof DeliveryLockActivationError && error.code === 'BELOW_PAR') {
        // Drop into candidate mode on that run so the aside shows the
        // shortfall and the remedies; the run's display row is what the
        // picker inspects.
        const run = materialRuns.find((r) => r.snapshots.some((s) => s.id === version.id));
        setSelectedLockVersionId(run?.displayRepresentative.id ?? version.id);
        toast.error('Below par — cover the shortfall before setting active');
        return;
      }
      throw error;
    }
  }, [
    parGuardEnabled,
    loan.id,
    loan.encompass_loan_id,
    loan.organization_id,
    activeSelectionForCurrentLoan?.lockVersionId,
    materialRuns,
    applyActivatedSelection
  ]);

  // After every Set Active & Save attempt: re-read the active selection and
  // leave candidate mode only when the submitted target is confirmed active.
  // A rejection keeps the candidate and its draft (clearing the selected id
  // would change the workspace key and remount the draft); a read error
  // keeps the previous selection.
  const handleCandidateActivated = useCallback(async (targetLockVersionId: string) => {
    if (!loan.encompass_loan_id) {
      return;
    }
    let next: DeliveryLockSelection | null;
    try {
      next = await getActiveDeliveryLockSelectionStrict(loan.encompass_loan_id);
    } catch (error) {
      console.warn('[LoanDetailsSheet] Failed to re-read the active delivery lock', error);
      return;
    }
    if (!next) {
      return;
    }
    if (next.lockVersionId !== targetLockVersionId) {
      // Someone else switched the lock (STALE_ACTIVE_SELECTION): take the
      // authoritative selection so the next attempt sends the right expected
      // lock, but keep the inspected candidate and its draft.
      setDeliveryLockSelection(next);
      return;
    }
    applyActivatedSelection(next);
  }, [loan.encompass_loan_id, applyActivatedSelection]);

  // Workspace instance identity. With the guard on, key by material run so
  // candidate B and active B share one instance (submitted snapshot and
  // readback state survive activation); inspecting a different run still
  // remounts. Flag off keeps the legacy key.
  const workspaceKey = parGuardEnabled
    ? (selectedRun ?? activeRun)?.setActiveRepresentative.id ?? selectedLockVersionId ?? 'current'
    : selectedLockVersionId
      ?? (activeSelectionForCurrentLoan
        ? `delivery-${activeSelectionForCurrentLoan.id}-${activeSelectionForCurrentLoan.updatedAt}`
        : 'current');
  const historicalInspection = !!selectedLockVersionId && !candidateVersion;

  const leaveNeedsConfirm = isOpen && activeTab === 'pricing' && pricingHasUnsavedEdits;
  useBeforeUnloadWarning(leaveNeedsConfirm);

  const performLeave = useCallback((leave: PendingLeave) => {
    if (leave.kind === 'tab') {
      setActiveTab(leave.tab);
    } else {
      onClose();
    }
  }, [onClose]);

  const requestLeave = useCallback((leave: PendingLeave) => {
    if (leave.kind === 'tab' && leave.tab === activeTab) {
      return;
    }
    if (!leaveNeedsConfirm) {
      performLeave(leave);
      return;
    }
    leaveFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setPendingLeave(leave);
  }, [activeTab, leaveNeedsConfirm, performLeave]);

  const discardAndLeave = useCallback(() => {
    const leave = pendingLeave;
    setPendingLeave(null);
    setPricingHasUnsavedEdits(false);
    if (leave) {
      performLeave(leave);
    }
  }, [pendingLeave, performLeave]);

  // Role-based tab visibility
  const { getCurrentUser, hasAnyPermission, isLoadingPermissions } = useRoleAccess();
  const currentUser = getCurrentUser();
  const canViewCompensation = currentUser?.roles?.some(role =>
    ['admin', 'loan_officer'].includes(role)
  ) ?? false;
  const canViewProductPricing = hasAnyPermission(['view_product_pricing']);
  const canGenerateReports = hasAnyPermission(['generate_reports']);
  const canReviewFraudAlerts = hasAnyPermission(['review_fraud_alerts']);
  const openFraudAlertCount = fraudAlerts.filter(alert => alert.reviewStatus === 'open').length;

  // Doc Order tab: view_doc_orders permission + DB flag + Banked-Retail channel.
  // Channel test uses the same core helper as the doc-order edge function so
  // the tab gate and the server gate can never disagree on formatting.
  const canViewDocOrders = hasAnyPermission(['view_doc_orders']);
  const docOrderFlag = useFeatureFlag('doc_order_v1');
  const emailHistoryFlag = useFeatureFlag('loan_email_activity_v1');
  const reminderPauseFlag = useFeatureFlag('borrower_disclosure_reminder_pause_v1');
  const canShowReminderPause = !reminderPauseFlag.loading && reminderPauseFlag.enabled;
  const drawerOpener = useRef<HTMLElement | null>(null);
  const canShowEmailHistory = !emailHistoryFlag.loading && emailHistoryFlag.enabled;
  const isBankedRetail = isDocOrderChannel(loan.channel);
  const canShowDocOrder = canViewDocOrders && docOrderFlag.enabled && isBankedRetail;

  // Filter tabs based on role / permissions
  const visibleTabs = useMemo(() => {
    return TABS.filter(tab => {
      if (tab.id === 'communications') {
        return canShowEmailHistory || canShowReminderPause;
      }
      if (tab.id === 'pricing') {
return canViewProductPricing;
}
      if (tab.id === 'compensation') {
return canViewCompensation;
}
      if (tab.id === 'docorder') {
return canShowDocOrder;
}
      return true;
    });
  }, [canViewProductPricing, canViewCompensation, canShowDocOrder, canShowEmailHistory, canShowReminderPause]);

  useEffect(() => {
    if (activeTab === 'communications' && !emailHistoryFlag.loading && !reminderPauseFlag.loading && !canShowEmailHistory && !canShowReminderPause) {
      setActiveTab('form1008');
    }
  }, [activeTab, emailHistoryFlag.loading, reminderPauseFlag.loading, canShowEmailHistory, canShowReminderPause]);

  // Defensive fallback: if user lacks pricing permission after load, redirect to form1008
  useEffect(() => {
    if (activeTab === 'pricing' && !isLoadingPermissions && !canViewProductPricing) {
      setActiveTab('form1008');
    }
  }, [activeTab, isLoadingPermissions, canViewProductPricing]);

  // Same defensive redirect for the Doc Order tab once its gates resolve.
  useEffect(() => {
    if (activeTab === 'docorder' && !isLoadingPermissions && !docOrderFlag.loading && !canShowDocOrder) {
      setActiveTab('form1008');
    }
  }, [activeTab, isLoadingPermissions, docOrderFlag.loading, canShowDocOrder]);

  useEffect(() => {
    if (
      activeTab === 'fraudAlerts'
      && fraudAlertsLoaded
      && !fraudAlertsLoading
      && !fraudAlertsError
      && fraudAlerts.length === 0
    ) {
      setActiveTab('form1008');
    }
  }, [activeTab, fraudAlerts.length, fraudAlertsError, fraudAlertsLoaded, fraudAlertsLoading]);

  // Check if loan has LE fields data
  const hasLeFields = loan.le_fields && Object.keys(loan.le_fields).length > 0;

  // Format borrower name for header
  const borrowerName = [loan.borrower_first_name, loan.borrower_last_name]
    .filter(Boolean)
    .join(' ') || 'Unknown Borrower';

  // Check if loan is locked for status indicator
  const isLocked = loan.rate_lock_status === 'Locked';
  // Synced verbatim from Encompass custom field CX.LOAN.STATUS (same source as
  // the Loan Center "Loan Status" column); blank sync values count as missing.
  const loanStatus = loan.cx_loan_status?.trim() || null;
  const quickStatsLoan = activeTab === 'pricing' && canViewProductPricing ? pricingLoan : loan;

  return (
    <>
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          requestLeave({ kind: 'close' });
        }
      }}
    >
      <SheetContent
        onOpenAutoFocus={() => {
          drawerOpener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        }}
        onCloseAutoFocus={(event) => {
          if (onCloseAutoFocus) {
            onCloseAutoFocus(event);
          } else if (drawerOpener.current?.isConnected) {
            event.preventDefault();
            drawerOpener.current.focus();
          }
        }}
        side="right"
        className="w-full sm:w-[92vw] sm:max-w-[1600px] p-0 flex flex-col [&>button]:text-white [&>button]:opacity-90 [&>button:hover]:opacity-100"
      >
        {/* Header */}
        <SheetHeader className="px-6 py-4 bg-[#00845c] text-white shrink-0 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 pr-6 sm:pr-0">
            <div className="min-w-0">
              <SheetTitle className="text-white text-xl">
                {borrowerName}
              </SheetTitle>
              <p className="text-white/75 text-sm mt-1">
                Loan Details
              </p>
            </div>
            <div className="mr-0 flex min-w-0 max-w-full flex-wrap items-center gap-3 sm:mr-8">
              <div className="flex min-w-0 max-w-full flex-col items-end leading-tight" data-testid="sheet-loan-status">
                <span className="text-[10px] font-medium uppercase tracking-wider text-white">Loan Status</span>
                <span className={cn('max-w-full whitespace-normal break-words text-right text-sm text-white', loanStatus && 'font-medium')}>
                  {loanStatus ?? '—'}
                </span>
              </div>
              {isLocked && (
                <Badge variant="secondary" className="bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-50">
                  <Lock className="h-3 w-3 mr-1" />
                  Locked
                </Badge>
              )}
              <Badge variant="secondary" className="bg-white text-zinc-950 hover:bg-white text-base px-3 py-1">
                {loan.trans_details_loan_number}
              </Badge>
              {loan.trans_details_loan_number && canGenerateReports && (
                <>
                  {onOpenDataExports && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="bg-white text-zinc-950 hover:bg-white/90"
                      onClick={onOpenDataExports}
                    >
                      <FileSpreadsheet className="mr-2 h-4 w-4" />
                      Data Export
                    </Button>
                  )}
                  <ClarifiExportButton
                    loanNumber={loan.trans_details_loan_number}
                    variant="secondary"
                    size="sm"
                    className="bg-white text-zinc-950 hover:bg-white/90"
                  />
                </>
              )}
            </div>
          </div>
          {activeTab === 'pricing' && canViewProductPricing && <PricingHeaderSummary loan={pricingLoan} />}
        </SheetHeader>

        {/* Main Content Area with Sidebar */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar - Tab Navigation */}
          <div className="hidden w-48 bg-muted/30 border-r shrink-0 py-4 sm:block">
            <nav className="space-y-1 px-2">
              {visibleTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const isFraudAlertsTab = tab.id === 'fraudAlerts';
                const isDisabled = isFraudAlertsTab
                  && fraudAlertsLoaded
                  && !fraudAlertsLoading
                  && !fraudAlertsError
                  && fraudAlerts.length === 0;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    disabled={isDisabled}
                    title={isDisabled ? 'No fraud alert history for this loan' : undefined}
                    onClick={() => requestLeave({ kind: 'tab', tab: tab.id })}
                    className={cn(
                      'w-full flex items-start gap-3 px-3 py-3 rounded-lg text-left transition-colors disabled:cursor-not-allowed disabled:opacity-45',
                      isActive
                        ? 'bg-[#00845c] text-white'
                        : isDisabled
                          ? 'bg-muted/40 text-muted-foreground'
                          : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <Icon className={cn(
                      'h-5 w-5 shrink-0 mt-0.5',
                      isActive ? 'text-white' : 'text-muted-foreground'
                    )} />
                    <div className="min-w-0">
                      <div className={cn(
                        'flex items-center gap-2 font-medium text-sm',
                        isActive ? 'text-white' : 'text-foreground'
                      )}>
                        {tab.label}
                        {isFraudAlertsTab && openFraudAlertCount > 0 && (
                          <span className={cn(
                            'inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold',
                            isActive
                              ? 'bg-white text-destructive'
                              : 'bg-destructive text-destructive-foreground'
                          )}>
                            {openFraudAlertCount}
                          </span>
                        )}
                      </div>
                      <div className={cn(
                        'text-xs truncate',
                        isActive ? 'text-white/70' : 'text-muted-foreground'
                      )}>
                        {tab.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </nav>

            {/* Quick Stats in Sidebar */}
            <div className="mt-6 px-4 space-y-3" data-testid="loan-quick-stats">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Quick Stats
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Loan Amount</span>
                  <span className="font-medium">
                    {formatWholeCurrency(quickStatsLoan.trans_details_loan_amt)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Rate</span>
                  <span className="font-medium">
                    {isPresent(lockRateValue(quickStatsLoan))
                      ? formatPercent(lockRateValue(quickStatsLoan), 3)
                      : isPresent(quickStatsLoan.trans_details_interest_rate)
                        ? formatPercent(quickStatsLoan.trans_details_interest_rate, 3)
                        : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">LTV</span>
                  <span className="font-medium">
                    {isPresent(quickStatsLoan.rate_lock_ltv)
                      ? formatPercent(quickStatsLoan.rate_lock_ltv, 2)
                      : isPresent(quickStatsLoan.freddie_mac_ltv_ratio_pct)
                        ? formatPercent(quickStatsLoan.freddie_mac_ltv_ratio_pct, 2)
                        : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">FICO</span>
                  <span className="font-medium">
                    {quickStatsLoan.rate_lock_min_fico
                      || quickStatsLoan.cx_loan_level_fico
                      || quickStatsLoan.representative_credit_score
                      || '-'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content Area */}
          <ScrollArea
            className={cn(
              'flex-1 min-w-0 h-[calc(100vh-100px)]',
              activeTab === 'pricing' && 'bg-[oklch(0.985_0.004_85)]'
            )}
          >
            <div className="border-b bg-background p-3 sm:hidden">
              <label htmlFor="loan-details-mobile-section" className="sr-only">Loan details section</label>
              <select id="loan-details-mobile-section" value={activeTab} onChange={(event) => requestLeave({ kind: 'tab', tab: event.target.value as TabId })} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                {visibleTabs.map((tab) => <option key={tab.id} value={tab.id} disabled={tab.id === 'fraudAlerts' && fraudAlertsLoaded && !fraudAlertsLoading && !fraudAlertsError && fraudAlerts.length === 0}>{tab.label}</option>)}
              </select>
            </div>
            <div className={cn('min-w-0', activeTab !== 'appraisal' && 'p-6')}>
              {activeTab === 'form1008' && <Form1008Content loan={effectiveLoan} />}
              {activeTab === 'team' && <LoanTeamTab loan={effectiveLoan} />}
              {isOpen && activeTab === 'communications' && loan.encompass_loan_id && (
                <div key={loan.encompass_loan_id} className='space-y-6'>
                  {canShowReminderPause && <DisclosureReminderControls loanId={loan.encompass_loan_id} />}
                  {canShowEmailHistory && <LoanEmailHistory loanId={loan.encompass_loan_id} />}
                </div>
              )}
              {activeTab === 'pricing' && (
                canViewProductPricing ? (
                  <ProductPricingWorkspace
                    key={workspaceKey}
                    loan={pricingLoan}
                    currentLoan={selectedLockVersionId ? loan : undefined}
                    onLoanUpdate={historicalInspection ? undefined : handleLoanUpdate}
                    onAuthoritativeRefresh={historicalInspection ? undefined : refreshAuthoritativeLoan}
                    lockVersions={visibleLockVersions}
                    selectedLockVersionId={selectedLockVersionId}
                    onLockVersionSelect={setSelectedLockVersionId}
                    isViewingSnapshot={historicalInspection}
                    deliveryLockSelection={activeSelectionForCurrentLoan}
                    isDeliveryLockMode={!selectedLockVersionId && !!activeSelectionForCurrentLoan}
                    onUseLockVersionAsDeliveryLock={handleUseLockVersionAsDeliveryLock}
                    candidateVersion={candidateVersion}
                    parGuardEnabled={parGuardEnabled}
                    onActivated={handleCandidateActivated}
                    liveLoan={loan}
                    onDirtyChange={setPricingHasUnsavedEdits}
                  />
                ) : isLoadingPermissions ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : null
              )}
              {activeTab === 'approval' && <ApprovalLetterTab loan={effectiveLoan} />}
              {activeTab === 'loanEstimate' && (
                hasLeFields ? (
                  <LoanEstimateViewer
                    loan={effectiveLoan}
                    loanNumber={effectiveLoan.trans_details_loan_number}
                  />
                ) : (
                  <LEViewerEmptyState message="No Loan Estimate data available. Run backfill to sync LE fields from Encompass." />
                )
              )}
              {activeTab === 'delivery' && (
                <InvestorDeliveryTab loan={effectiveLoan} onLoanUpdate={handleLoanUpdate} />
              )}
              {activeTab === 'compensation' && (
                <MyCompensationTab loan={effectiveLoan} onLoanUpdate={handleLoanUpdate} />
              )}
              {activeTab === 'docorder' && <DocOrderTab loan={effectiveLoan} />}
              {activeTab === 'appraisal' && <AppraisalTab key={loan.id} loanId={loan.id} encompassLoanId={loan.encompass_loan_id} initialReportId={initialAppraisalReportId} initialFeeVerificationId={initialFeeVerificationId} />}
              {activeTab === 'fraudAlerts' && (
                <FraudAlertsTab
                  alerts={fraudAlerts}
                  loading={fraudAlertsLoading}
                  loadError={fraudAlertsError}
                  canReview={canReviewFraudAlerts}
                  onRefresh={refreshFraudAlerts}
                  onOpenLoanDocuments={onOpenLoanDocuments}
                />
              )}
            </div>
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
    <AlertDialog
      open={pendingLeave !== null}
      onOpenChange={(open) => {
        if (!open) {
          setPendingLeave(null);
        }
      }}
    >
      <AlertDialogContent
        onCloseAutoFocus={(event) => {
          // Opened from code, so there is no trigger to return to: go back to
          // the tab or close button the user pressed.
          event.preventDefault();
          if (leaveFocusRef.current?.isConnected) {
            leaveFocusRef.current.focus();
          }
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
          <AlertDialogDescription>
            Your Product &amp; Pricing changes haven&apos;t been saved. Stay to keep editing, or discard them.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Stay</AlertDialogCancel>
          <AlertDialogAction className={buttonVariants({ variant: 'destructive' })} onClick={discardAndLeave}>
            Discard changes
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
